'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useSearchParams } from 'next/navigation';
import { useT } from '@/components/i18n-provider';
import { X, ArrowLeft } from 'lucide-react';
import { cn } from '@/lib/utils';
import { AccountView, type AccountDeepLink } from '@/components/account/account-view';
import { AdminView } from '@/components/admin/admin-view';
import { SettingsView } from '@/components/settings/settings-view';
import { DiscoverHub } from '@/components/discover/discover-hub';
import { LocalView } from '@/components/local/local-view';
import { NotificationsView } from '@/components/notifications/notifications-view';
import {
  ProfilePreview,
  ProfilePreviewFooter,
  PostPreview,
  PostPreviewFooter,
  ContentPreview,
  ContentPreviewFooter,
} from './preview-dialog';
import { useWorkspaceAnon } from './anon-context';
import { WorkspaceDialogOpenContext } from './canvas-cover';
import { LoginDialogBody } from '@/components/auth/login-dialog-body';
import { MyContentManager } from './panels/my-content-manager';
import { SearchView } from './panels/search-view';
import { CreateAgentForm } from '@/components/create/create-forms';
import { AgentSettingsDialog } from './agent-settings-dialog';
import { GuidelinesView } from '@/components/legal/guidelines-view';
import { LeaderboardView } from '@/components/leaderboard/leaderboard-view';

type FullscreenNavCtx = {
  openHref: (href: string) => void;
  close: () => void;
  isOpen: boolean;
};

const Ctx = createContext<FullscreenNavCtx | null>(null);

/**
 * Mapping from in-app pathname → { friendly title, React node } that
 * renders inside the workspace dialog when the user navigates to that
 * href.
 *
 * The standalone /wallet, /analytics, /settings and /admin pages are
 * being retired — the workspace is the only UI surface going forward —
 * so the dialog header shows a friendly title (not the URL) and we
 * intentionally do NOT expose a "Open in new tab" affordance.
 *
 * Anything not in this registry falls back to a sandboxed iframe so
 * forward-looking callers can still pass an arbitrary URL without us
 * first wiring it up. That fallback is for pages that RENDER on their
 * own (`/doc/*`, `/docs/*`). It is never right for a
 * `redirectToWorkspaceDialog` shell — see the note above the
 * `/content-list` group.
 */
/**
 * `size` controls the dialog frame:
 *  - `wide` (default): 1200px max — used for the long-form views
 *    (Settings, Wallet, Analytics, etc.) which fill a 92vh column
 *    with their own scrolling subsections.
 *  - `compact`: shrink-to-content width capped at ~560px so the
 *    panel feels like a focused mini-dialog rather than a desktop
 *    page taking over the workspace. Install app uses this because
 *    its content tops out at `max-w-lg` (≈512px) and would look
 *    lost in a 1200px frame.
 */
type DialogSize = 'wide' | 'compact';
/**
 * `node` may be either a static `ReactNode` or a function of the
 * incoming URL's search params. The function form lets a single
 * pathname (e.g. `/local`) render different content depending on the
 * caller's intent, without polluting the registry with separate keys.
 *
 * `title` follows the same shape so the dialog header can adapt too -
 * e.g. `/local?context=terminal` renders as Install Robutler
 * instead of the generic Install app. Its function form also receives the
 * translator, so a title can come from en.json: the registry is a module
 * constant and cannot call `useT()` itself (final-portal-ui, 2026-09-28:
 * "New agent" and "Agent settings" were English in every locale).
 */
type Renderable<T> = T | ((params: URLSearchParams) => T);
type Translate = (key: string, params?: Record<string, string | number>) => string;
type TitleField = string | ((params: URLSearchParams, t: Translate) => string);
type RegistryEntry = {
  title: TitleField;
  node: Renderable<ReactNode>;
  /**
   * Optional sticky bottom-bar — used by preview dialogs to show
   * the "Add to workspace" / Close CTAs without the body needing
   * to know it's inside a dialog frame.
   */
  footer?: Renderable<ReactNode>;
  size?: DialogSize;
  /**
   * When true, opening this dialog while `anonReadOnly` is active
   * substitutes `/signup` instead. Used for views that have no
   * meaning without an account (notifications, wallet, etc.).
   */
  authRequired?: boolean;
  /**
   * Suppress the visible header title row while keeping the close
   * (and optional back) button anchored top-right. Used for dialog
   * bodies that already render their own hero heading inline —
   * `/login` and `/signup` have a centered Robutler logo + welcome
   * line, so the frame's small grey title above it reads as
   * duplicated noise. The `aria-label` on the dialog still uses
   * the registry's `title` for assistive tech.
   */
  hideHeaderTitle?: boolean;
};

/**
 * Set of pathnames that should redirect to `/signup` when an anon
 * visitor opens them via the deep-link consumer. These dialogs are
 * meaningless without an account — `Notifications` has nothing to
 * show, `Wallet` has no balance, `Settings` has no account. The list
 * is small enough to live next to the registry and avoid scattering
 * the rule through individual component renderers.
 */
const ANON_REDIRECT_TO_SIGNUP = new Set<string>([
  '/notifications',
  '/wallet',
  '/settings',
  '/analytics',
  '/admin',
  '/account',
  '/connect',
  '/developer',
  '/referrals',
  '/my-posts',
  '/following',
  '/submit',
  '/create',
  '/create-agent',
  '/channels/create',
  '/edit-profile',
  '/agent-settings',
  '/edit-post',
]);

const DIALOG_REGISTRY: Record<string, RegistryEntry> = {
  // Canonical "Account" hub — single dialog with tabbed Wallet /
  // Analytics sections. The legacy `/wallet` and `/analytics` entries
  // below alias to the same body with a different default tab so
  // existing bookmarks, the Stripe-return `?wallet=1` URL signal
  // (see ~line 322), and the standalone
  // `app/(portal)/(main)/{wallet,analytics}/page.tsx` redirects all
  // keep working.
  //
  // `/admin` is NOT an Account alias any more: it is its own dialog
  // below. Admin is a staff surface, not a personal-account one, and
  // nesting it here put it two clicks deep behind a nav of personal
  // concerns while AdminView rendered its own second-level nav inside
  // this one. It is reached from the user menu.
  '/account': {
    title: 'Account',
    node: (params) => (
      <AccountView
        embedded
        section={(params.get('section') as AccountDeepLink | null) ?? 'wallet'}
      />
    ),
  },
  // Function form so `?section=` survives (same reason as `/settings`
  // below): the over-limit reply from the MCP `delegate` tool links to
  // `/wallet?section=spending`, the per-agent limits, and the standalone
  // `/wallet` redirect forwards that param here through `?wallet=1`.
  '/wallet': {
    title: 'Account',
    node: (params) => (
      <AccountView
        embedded
        section={(params.get('section') as AccountDeepLink | null) ?? 'wallet'}
      />
    ),
  },
  '/analytics': {
    title: 'Account',
    node: <AccountView embedded section="analytics" />,
  },
  // Its own dialog. AdminView enforces the staff gate on every tab, and each
  // route behind those tabs re-checks the role server-side.
  '/admin': {
    title: 'Admin',
    node: <AdminView embedded />,
  },
  // Function form so `?tab=` survives: the deep-link consumer strips the query
  // from the URL bar before SettingsView mounts, so the tab has to be handed
  // over explicitly rather than re-read from `window.location`.
  '/settings': {
    title: 'Settings',
    node: (params) => <SettingsView embedded initialTab={params.get('tab') ?? undefined} />,
  },
  '/discover': {
    title: 'Discover',
    node: (
      <DiscoverHub
        preset={{ title: '', type: 'all', scope: 'everyone', showFeatured: true }}
      />
    ),
  },
  '/notifications': { title: 'Notifications', node: <NotificationsView showTitle={false} /> },

  // -----------------------------------------------------------------
  // Panels that only ever existed inside the workspace, reached by URL
  // through `DIALOG_ALIASES` (`lib/workspaces/url-actions.ts`).
  //
  // These MUST have entries. Their `app/(portal)/(main)/*` pages are
  // bare `redirectToWorkspaceDialog` shells with no body left, so the
  // iframe fallback below would re-request the shell, get redirected
  // back into the workspace, and render the app inside itself. The
  // fallback is for real standalone pages (`/doc/*`, `/docs/*`) only.
  // -----------------------------------------------------------------
  '/content-list': { title: 'Files', node: <MyContentManager /> },
  '/search': { title: 'Search', node: <SearchView /> },
  '/my-posts': {
    title: 'My posts',
    node: <DiscoverHub preset={{ title: '', type: 'posts', scope: 'mine' }} />,
  },
  '/following': {
    title: 'Following',
    node: <DiscoverHub preset={{ title: '', type: 'all', scope: 'following' }} />,
  },
  '/create-agent': {
    title: (_params, t) => t('workspace.createWith.newAgent'),
    node: <CreateAgentForm />,
    size: 'compact',
  },
  // `/u/<agent>/edit` and `?dialog=edit-profile&username=<agent>`: THAT
  // agent's settings, reached through the dialog action's `edit-profile`
  // branch (`lib/workspaces/url-actions.ts`) and from the create form's
  // success state. The person's own handle falls back to the Profile tab
  // of Settings inside `AgentSettingsDialog`.
  '/agent-settings': {
    title: (params, t) => {
      const u = params.get('username');
      return u ? `@${u}` : t('user.agentSettingsLabel');
    },
    node: (params) => <AgentSettingsDialog username={params.get('username') ?? ''} />,
  },
  // Not auth-only: moderation notices link people here, and a reported user
  // may well be reading it logged out.
  '/guidelines': { title: 'Community Guidelines', node: <GuidelinesView embedded /> },
  '/leaderboard': { title: 'Leaderboard', node: <LeaderboardView /> },
  '/tag': {
    title: (params) => `#${params.get('name') ?? 'tag'}`,
    node: (params) => (
      <DiscoverHub
        preset={{ title: '', type: 'all', tags: [params.get('name') ?? ''] }}
      />
    ),
  },
  '/local': {
    // One pathname, three intents. `context=agent` is the create form's
    // "create a local agent" link: it used to open this same view under
    // "Install app", which is not what the person asked for (portal visual
    // pass, 2026-09-28).
    title: (params, t) => {
      const context = params.get('context');
      if (context === 'terminal') return t('workspace.fullscreen.installRobutler');
      if (context === 'agent') return t('local.title');
      return t('nav.installApp');
    },
    // `desktopOnly` variant strips out everything except the desktop
    // app section — used when the caller (today: the workspace
    // terminal widget) only needs the user to install the local
    // daemon. `agentOnly` keeps only how to run your own agent. The
    // generic version still renders the full landing.
    node: (params) => (
      <LocalView
        embedded
        desktopOnly={params.get('context') === 'terminal'}
        agentOnly={params.get('context') === 'agent'}
      />
    ),
    size: 'compact',
  },

  // -----------------------------------------------------------------
  // Preview dialogs (Phase 2)
  //
  // Authed users landing on a shareable deep-link route
  // (`/u/<name>`, `/p/<id>`, `/content/<id>`) are server-side
  // redirected to `/workspace/<id>?preview=<kind>&...`, the
  // deep-link consumer translates that into
  // `openHref('/preview/<kind>?...')`, and these entries render a
  // compact read-only card plus an "Add to workspace" CTA bar.
  // The CTA spawns the corresponding canvas node and closes the
  // dialog; dismiss just closes without touching the canvas.
  // -----------------------------------------------------------------
  '/preview/profile': {
    title: (params) => {
      const u = (params.get('username') ?? '').replace(/^@+/, '');
      return u ? `@${u}` : 'Profile preview';
    },
    node: (params) => <ProfilePreview params={params} />,
    footer: (params) => <ProfilePreviewFooter params={params} />,
    size: 'compact',
  },
  '/preview/post': {
    title: 'Post preview',
    node: (params) => <PostPreview params={params} />,
    footer: (params) => <PostPreviewFooter params={params} />,
    size: 'compact',
  },
  '/preview/content': {
    title: 'Content preview',
    node: (params) => <ContentPreview params={params} />,
    footer: (params) => <ContentPreviewFooter params={params} />,
    size: 'compact',
  },

  // -----------------------------------------------------------------
  // Auth dialogs (Phase 5)
  //
  // `/login` and `/signup` are the same body — only the mode flag and
  // the analytic event differ. The standalone `app/(auth)/login`
  // route still exists as an SSR fallback for crawlers and the OAuth
  // callback round-trip, but inside the workspace these dialog
  // entries are the canonical UX.
  // -----------------------------------------------------------------
  '/login': {
    title: 'Log in',
    node: (params) => <LoginDialogBody params={params} mode="login" />,
    size: 'compact',
    hideHeaderTitle: true,
  },
  '/signup': {
    title: 'Sign up',
    node: (params) => <LoginDialogBody params={params} mode="signup" />,
    size: 'compact',
    hideHeaderTitle: true,
  },

  // -----------------------------------------------------------------
  // Auth-only dialog placeholders (Phase 1)
  //
  // These pathnames are reached via `?dialog=<name>` from the
  // server-side redirects shipped in Phase 4. Most of them have
  // existing full-page implementations under `app/(portal)/(main)`
  // that we're retiring; the entries below name them so the
  // FullscreenNavProvider stops falling through to its iframe
  // fallback (which still works, but lacks the friendly title and
  // takes one extra request). Each value reuses the page-level
  // component embedded inside the dialog frame so we don't have to
  // re-implement the body twice.
  //
  // DO NOT leave a `dialog=` name out and expect the iframe fallback
  // to cover it. Those pages are pure `redirectToWorkspaceDialog`
  // shells: the iframe re-requests the shell, the shell redirects
  // back to `/workspace?dialog=<name>`, the nested workspace mounts
  // its own deep-link consumer, and the app renders inside itself.
  // `/referrals` reached ten levels before the screen gave out. Every
  // shell name now resolves through `DIALOG_ALIASES` in
  // `lib/workspaces/url-actions.ts` to an entry above, and
  // `redirectToWorkspaceDialog` 404s rather than redirect when it
  // sees `embed=workspace`, so a missed name costs one empty panel
  // instead of a runaway.
  // -----------------------------------------------------------------
};

function parseHref(href: string): { pathname: string; params: URLSearchParams } {
  try {
    const u = new URL(
      href,
      typeof window !== 'undefined' ? window.location.origin : 'http://localhost',
    );
    return { pathname: u.pathname, params: u.searchParams };
  } catch {
    return { pathname: href, params: new URLSearchParams() };
  }
}

function resolveRegistryEntry(
  href: string,
): { entry: RegistryEntry; params: URLSearchParams } | null {
  // Match by pathname only so links carrying query/hash still resolve
  // (e.g. `/settings?tab=tokens`); the search params are forwarded to
  // function-form `node` / `title` so entries can branch on them.
  const { pathname, params } = parseHref(href);
  const entry = DIALOG_REGISTRY[pathname];
  return entry ? { entry, params } : null;
}

function renderField<T>(field: Renderable<T>, params: URLSearchParams): T {
  return typeof field === 'function' ? (field as (p: URLSearchParams) => T)(params) : field;
}

/** A registry title, translated when its entry names a key. Exported for tests. */
export function renderRegistryTitle(href: string, t: Translate): string | null {
  const resolved = resolveRegistryEntry(href);
  if (!resolved) return null;
  return renderTitle(resolved.entry.title, resolved.params, t);
}

function renderTitle(field: TitleField, params: URLSearchParams, t: Translate): string {
  return typeof field === 'function' ? field(params, t) : field;
}

/**
 * Workspace-scoped navigation context.
 *
 * Inside the workspace, the user shouldn't be ripped out of the canvas to
 * inspect Wallet, Analytics, Settings, or Admin. Instead, those views
 * render inside an in-page dialog so the workspace (and its in-flight
 * chats) stays mounted. Known destinations render their core view
 * components directly; unknown destinations still fall back to an
 * iframe for backward compatibility.
 */
export function FullscreenNavProvider({
  children,
  initialOpenHref,
  initialPreview,
}: {
  children: React.ReactNode;
  /**
   * Seed the dialog stack on first render. Used by the anon
   * shareable routes (`/u/<name>`, `/p/<id>`, `/content/<id>`) to
   * open the SSR preview dialog before client JS hydrates so
   * crawlers see the canonical body inside the dialog HTML.
   */
  initialOpenHref?: string;
  /**
   * SSR-rendered preview body keyed by the dialog href it should
   * override. When `top === initialPreview.href`, the dialog renders
   * `initialPreview.body` instead of the registry's client-side
   * `node`. The registry's footer / title still apply.
   */
  initialPreview?: { href: string; body: ReactNode; footer?: ReactNode };
}) {
  const t = useT();
  const [hrefStack, setHrefStack] = useState<string[]>(
    initialOpenHref ? [initialOpenHref] : [],
  );
  const { anonReadOnly } = useWorkspaceAnon();

  const openHref = useCallback(
    (href: string) => {
      // Anon visitors trying to open an auth-only dialog get
      // redirected to /signup so we don't render an empty shell of
      // someone else's settings page.
      const { pathname } = parseHref(href);
      const next =
        anonReadOnly && ANON_REDIRECT_TO_SIGNUP.has(pathname) ? '/signup' : href;
      setHrefStack((prev) => [...prev, next]);
    },
    [anonReadOnly],
  );
  const close = useCallback(() => setHrefStack([]), []);
  const goBack = useCallback(() => {
    setHrefStack((prev) => (prev.length > 1 ? prev.slice(0, -1) : []));
  }, []);

  // ─────────────────────────────────────────────────────────────────
  // URL-signal → wallet dialog
  //
  // Stripe Checkout + Customer Portal returns land on
  // `/workspace?wallet=1[&payment=success&session_id=…&plan_switched=true]`.
  // The legacy `/wallet` page is being retired in favor of the
  // workspace wallet dialog, so we read the `?wallet=1` flag here and
  // dispatch into the dialog stack — forwarding `payment`,
  // `session_id`, and `plan_switched` so the embedded `WalletView`
  // can run its verify-payment / balance-poll / highlight pipeline.
  //
  // The `consumed` ref pins this to a one-shot: re-renders triggered
  // by the subsequent `history.replaceState` (which strips the
  // signal from the URL) won't re-fire the effect, and a refresh
  // after consumption no longer carries the signal so the dialog
  // stays closed unless the user opens it again.
  // ─────────────────────────────────────────────────────────────────
  const searchParams = useSearchParams();
  const walletSignalConsumed = useRef(false);

  useEffect(() => {
    if (walletSignalConsumed.current) return;
    if (!searchParams || searchParams.get('wallet') !== '1') return;
    walletSignalConsumed.current = true;

    // Build the dialog href, forwarding everything EXCEPT our own
    // `wallet=1` open-signal (the embedded view doesn't need it).
    const dialogParams = new URLSearchParams();
    for (const [k, v] of searchParams.entries()) {
      if (k === 'wallet') continue;
      dialogParams.set(k, v);
    }
    const qs = dialogParams.toString();
    openHref(qs ? `/wallet?${qs}` : '/wallet');

    // Strip the signal + payment params from the browser URL so a
    // refresh doesn't reopen the dialog or re-trigger verify-payment.
    // `replaceState` (not router.replace) keeps this purely client-side
    // and avoids a re-render storm in the workspace tree.
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      url.searchParams.delete('wallet');
      url.searchParams.delete('payment');
      url.searchParams.delete('session_id');
      url.searchParams.delete('plan_switched');
      url.searchParams.delete('section');
      const next = url.pathname + (url.search || '') + url.hash;
      window.history.replaceState(null, '', next);
    }
  }, [searchParams, openHref]);

  // Esc to close.
  useEffect(() => {
    if (hrefStack.length === 0) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (hrefStack.length > 1) goBack();
        else close();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [hrefStack.length, close, goBack]);

  const value = useMemo<FullscreenNavCtx>(
    () => ({ openHref, close, isOpen: hrefStack.length > 0 }),
    [openHref, close, hrefStack.length],
  );

  const top = hrefStack[hrefStack.length - 1];
  const resolved = useMemo(() => (top ? resolveRegistryEntry(top) : null), [top]);
  const registryEntry = resolved?.entry ?? null;
  const registryParams = resolved?.params ?? null;

  // Iframe fallback for hrefs without a registry entry — kept so any
  // forward-looking caller can pass an arbitrary URL without us first
  // wiring it up. Appends `embed=workspace` so the embedded page can
  // suppress its own chrome if it wants to.
  const iframeSrc = useMemo(() => {
    if (!top || registryEntry) return '';
    try {
      const u = new URL(top, typeof window !== 'undefined' ? window.location.origin : 'http://localhost');
      u.searchParams.set('embed', 'workspace');
      return u.pathname + u.search + u.hash;
    } catch {
      return top;
    }
  }, [top, registryEntry]);

  // Friendly title for the dialog header. Registry entries supply it
  // directly; iframe fallbacks derive a humanised label from the
  // pathname so we never show the bare URL.
  const headerTitle = useMemo<string>(() => {
    if (!top) return '';
    if (registryEntry)
      return renderTitle(registryEntry.title, registryParams ?? new URLSearchParams(), t);
    try {
      const u = new URL(
        top,
        typeof window !== 'undefined'
          ? window.location.origin
          : 'http://localhost',
      );
      const first = u.pathname.split('/').filter(Boolean)[0] ?? '';
      return first ? first[0].toUpperCase() + first.slice(1) : top;
    } catch {
      return top;
    }
    // `t` changes with the locale; a translated title follows it.
  }, [top, registryEntry, registryParams, t]);

  return (
    <Ctx.Provider value={value}>
      {/* While a dialog is up it covers the canvas: the ad slot reads this
          (components/workspace/canvas-cover.ts) and stays inactive, so an ad
          is never requested, drawn or counted under the dialog. */}
      <WorkspaceDialogOpenContext.Provider value={!!top}>
      {children}
      {top && (
        <div
          // An app's sign-in inside (`data-named`) is anchored at the top so
          // "Other ways to sign in" grows it downwards instead of moving it
          // under the pointer (components/auth/login-dialog-body.tsx).
          className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm has-[[data-named]]:items-start has-[[data-named]]:pt-[10vh]"
          role="dialog"
          aria-modal
          aria-label={headerTitle}
          data-testid="workspace-fullscreen-dialog"
          data-dialog-href={top}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) close();
          }}
        >
          <div
            className={cn(
              'glass-panel relative flex flex-col overflow-hidden rounded-2xl shadow-2xl',
              // Compact dialogs shrink to their content with a tight
              // max-width — the body still owns its own max-height so
              // long forms can scroll inside the panel rather than
              // overflowing the viewport.
              registryEntry?.size === 'compact'
                ? 'h-auto max-h-[92vh] w-[min(560px,96vw)]'
                : 'h-[92vh] w-[min(1200px,96vw)]',
            )}
          >
            {/*
              When `hideHeaderTitle` is set the dialog body owns its
              own heading (e.g. /login + /signup show a centered
              logo + welcome line), so we render a compact floating
              control strip top-right instead of a full header row.
              The body then has the full panel width to breathe.
            */}
            {registryEntry?.hideHeaderTitle ? (
              <div className="pointer-events-none absolute right-2 top-2 z-10 flex items-center gap-1">
                {hrefStack.length > 1 && (
                  <button
                    type="button"
                    onClick={goBack}
                    className="pointer-events-auto rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground"
                    aria-label={t('user.back')}
                  >
                    <ArrowLeft className="h-4 w-4" />
                  </button>
                )}
                <button
                  type="button"
                  onClick={close}
                  className="pointer-events-auto rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground"
                  aria-label={t('workspace.canvas.node.close')}
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <div className="flex shrink-0 items-center gap-2 px-4 py-3 shadow-[inset_0_-1px_0_rgba(15,23,42,0.06)] dark:shadow-[inset_0_-1px_0_rgba(0,0,0,0.35)]">
                {hrefStack.length > 1 && (
                  <button
                    type="button"
                    onClick={goBack}
                    className="rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground"
                    aria-label={t('user.back')}
                  >
                    <ArrowLeft className="h-4 w-4" />
                  </button>
                )}
                {/*
                  Friendly dialog title — the standalone /wallet,
                  /analytics, /settings, /admin pages are being
                  retired and the workspace is the only UI surface,
                  so we don't expose an "Open in new tab"
                  affordance any more.
                */}
                <h2 className="min-w-0 flex-1 truncate text-sm font-semibold tracking-tight text-foreground">
                  {headerTitle}
                </h2>
                <button
                  type="button"
                  onClick={close}
                  className="rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground"
                  aria-label={t('workspace.canvas.node.close')}
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            )}
            {registryEntry ? (
              <>
                <div
                  className={cn(
                    'min-h-0 flex-1 overflow-y-auto @container/main',
                    // Dialogs that hide the chrome header own their
                    // own hero block (logo + welcome line for
                    // login / signup). They need more vertical
                    // breathing room so the panel doesn't look like
                    // a cropped sticker — bump py to ~40px on
                    // mobile, ~56px on desktop. Other dialogs keep
                    // the original tight padding.
                    registryEntry.hideHeaderTitle
                      ? 'px-6 py-10 sm:py-14'
                      : 'px-4 py-4',
                  )}
                >
                  {/*
                    SSR Twin override: when the active href matches
                    the SSR-prefetched preview's href, render the
                    server-rendered body so the canonical content
                    ships in the initial HTML (crawlable, no client
                    fetch needed). Authed in-canvas previews fall
                    through to the registry's client `node` as
                    before.
                  */}
                  {initialPreview && top === initialPreview.href
                    ? initialPreview.body
                    : renderField(
                        registryEntry.node,
                        registryParams ?? new URLSearchParams(),
                      )}
                </div>
                {(initialPreview && top === initialPreview.href
                  ? initialPreview.footer
                  : registryEntry.footer) && (
                  <div className="shrink-0">
                    {initialPreview && top === initialPreview.href
                      ? initialPreview.footer
                      : renderField(
                          registryEntry.footer!,
                          registryParams ?? new URLSearchParams(),
                        )}
                  </div>
                )}
              </>
            ) : (
              <iframe
                key={iframeSrc}
                src={iframeSrc}
                title={headerTitle}
                className="h-full w-full flex-1 border-0 bg-background"
                // Same-origin so cookies/auth ride along, but block top-level
                // navigation so a click inside the iframe can't escape the
                // workspace tab. `allow-modals` — without it the browser
                // SILENTLY ignores window.print() (and confirm/alert), so the
                // docs Print action did nothing in the maximized view.
                // `allow-downloads` — file exports (PDF/md) are blob-anchor
                // clicks that get silently dropped without the token.
                sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox allow-modals allow-downloads"
              />
            )}
          </div>
        </div>
      )}
      </WorkspaceDialogOpenContext.Provider>
    </Ctx.Provider>
  );
}

export function useFullscreenNav() {
  const ctx = useContext(Ctx);
  if (!ctx) {
    // Outside of a workspace, fall back to real navigation.
    return {
      openHref: (href: string) => {
        if (typeof window !== 'undefined') window.location.href = href;
      },
      close: () => {},
      isOpen: false,
    } satisfies FullscreenNavCtx;
  }
  return ctx;
}
